import {formatLogItems, getLogName, parseLogItems} from '@/modules/system-logs/log-message'
import {UserTag} from '@/shared/components/ui/tags/user-tag'
import {getDatetimeFormatter, joinLabels} from '@cypro/ui/utils'
import {Timeline} from '@chakra-ui/react'
import {EventLog} from '@cypro/engine/schema'
import {useTranslate} from '@tolgee/react'
import {LuCheck, LuPen, LuTrash, LuEye} from 'react-icons/lu'

const actionIcons = {
  create: <LuCheck />,
  update: <LuPen />,
  delete: <LuTrash />,
  access: <LuEye />,
} as const

const actionText = {
  create: 'created_by',
  update: 'updated_by',
  delete: 'deleted_by',
  access: 'accessed_by',
} as const

const customResource = (action: string, resource: string) => {
  return `${action}_${resource}`
}

const EVENTS_WITH_OWN_TITLE = new Set([
  'PATIENT_PROTOCOL_CYCLES_UPDATED',
  'PATIENT_PROTOCOL_STOPPED',
  'PATIENT_PROTOCOL_REACTIVATED',
  'PATIENT_TREATMENT_STOPPED',
])

const PRESCRIPTION_RESOURCES = new Set(['patient-therapy-sequence', 'patient-therapy-sequence-medicine'])

// Free-text reason a user gave for the change (e.g. stopping/removing a protocol, shifting a
// cycle), recorded on the event's `details` at write time.
function getEventReason(activity: EventLog): string | null {
  const details = activity.details as {reason?: string | null} | null | undefined
  return details?.reason || null
}

interface Props {
  activities: EventLog[]
}

export function PatientHistoryTimeline({activities}: Props) {
  const {t} = useTranslate(['common', 'activity'])
  const {t: tE} = useTranslate('events')

  function getTitle(activity: EventLog) {
    const details = activity.details ?? {}
    const isPrescription = PRESCRIPTION_RESOURCES.has(activity.resource ?? '')

    if (activity.event && (isPrescription || EVENTS_WITH_OWN_TITLE.has(activity.event))) {
      return joinLabels(
        [
          getLogName(activity, t, tE),
          details.protocolName,
          isPrescription ? (details.therapyInternalId ?? details.id) : null,
        ].filter(Boolean)
      )
    }
    if (activity.resource !== 'patient') return t(customResource(activity.action ?? '', activity.resource ?? ''))
    return activity.action && t(actionText[activity.action])
  }
  return (
    <Timeline.Root marginTop="5" size="xl">
      {activities.map(activity => {
        const itemsText = formatLogItems(parseLogItems(activity.details), t)
        return (
          <Timeline.Item key={activity.id}>
            <Timeline.Connector>
              <Timeline.Separator />
              <Timeline.Indicator bgColor="gray.muted" color="gray.fg">
                {activity.action && actionIcons[activity.action]}
              </Timeline.Indicator>
            </Timeline.Connector>
            <Timeline.Content>
              <Timeline.Title textStyle="sm">
                {getTitle(activity)}
                <UserTag user={activity.actor} />
              </Timeline.Title>
              <Timeline.Description>{getDatetimeFormatter().format(activity.performedAt)}</Timeline.Description>
              {itemsText && <Timeline.Description color="fg">{itemsText}</Timeline.Description>}
              {getEventReason(activity) && (
                <Timeline.Description color="fg" whiteSpace="pre-line">
                  {t('reason')}: {getEventReason(activity)}
                </Timeline.Description>
              )}
            </Timeline.Content>
          </Timeline.Item>
        )
      })}
    </Timeline.Root>
  )
}
